// RODAPÉ "Autoral" (rodape = 6): rodapé CLARO e editorial do modelo "Autoral" —
// 4 colunas separadas por divisórias verticais finas:
//  (a) logo grande do tenant;
//  (b) nome, endereço, telefones (WhatsApp quando marcado) e e-mail revelado no
//      clique ("Ver e-mail" — o endereço não vai cru no HTML, que é como a
//      referência do modelo protege o e-mail de coletor);
//  (c) "Menu" — os itens do site (transações + páginas do tenant + blog);
//  (d) "Social" — as redes do tenant em TEXTO (não ícone), do jeito editorial.
// Faixa inferior com direitos autorais e CRECI/CNPJ quando houver.
// Fundos: cor3 (conteúdo) + cor6 (faixa inferior), ambos com contraste
// adaptativo REAL (ensureReadableTextColor) — é o que deixa o rodapé funcionar
// claro neste modelo e escuro em qualquer outro tenant que escolher a variante.
// Default export = ErrorBoundary com fallback (padrão MegaFooter).
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { ensureReadableTextColor, isColorLight, MaskPhone } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import Popular from '../populares/Popular';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';
import styles from './AutoralFooter.module.css';

interface IAutoralFooter {
    site: any;
    allRodape: any;
    allCities?: any;
    codsite?: any;
    origin?: any;
    isMobile?: boolean;
    favorito?: any;
    hidePopular?: boolean;
    fallback?: React.ReactNode;
}

const AutoralFooterInner: React.FC<IAutoralFooter> = ({ site: siteProp, origin, codsite, allRodape, hidePopular }) => {
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const [isMounted, setIsMounted] = useState(false);
    const [emailVisivel, setEmailVisivel] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (href: string) => (isMounted ? appendCodsiteQuery(href, codsite ?? codsiteQuery, origin) : href);
    const trackFooter = (nome: string, cat: string) => () =>
        trackMetaEvent(site, 'ViewContent', { content_name: nome, content_category: cat });

    // contraste adaptativo REAL sobre cada fundo (funciona claro OU escuro).
    const corConteudo = site?.cor3 || '#ffffff';
    const corTexto = ensureReadableTextColor(isColorLight(corConteudo) ? site?.cor11 : site?.cor13, corConteudo) || '#3c4858';
    const corTitulo = ensureReadableTextColor(site?.cor2, corConteudo, 3.2) || corTexto;
    const corBarra = site?.cor6 || corConteudo;
    const corBarraText = ensureReadableTextColor(isColorLight(corBarra) ? site?.cor11 : site?.cor13, corBarra) || corTexto;

    const menus = (allDataHome?.menus || []).filter((m: any) => m?.Disponivel && m?.titulo).slice(0, 7);
    const socials = [
        { href: site?.Dados?.instagram, rede: 'Instagram' },
        { href: site?.Dados?.facebook, rede: 'Facebook' },
        { href: site?.Dados?.youtube, rede: 'Youtube' },
        { href: site?.Dados?.tiktok, rede: 'Tiktok' },
        { href: site?.Dados?.Linkedin, rede: 'Linkedin' },
    ].filter((s) => s.href);

    const phones = [
        { tel: site?.Dados?.tel1, nome: site?.Dados?.tel1_nome, whats: site?.Dados?.tel1_whats },
        { tel: site?.Dados?.tel2, nome: site?.Dados?.tel2_nome, whats: site?.Dados?.tel2_whats },
        { tel: site?.Dados?.tel3, nome: site?.Dados?.tel3_nome, whats: site?.Dados?.tel3_whats },
        { tel: site?.Dados?.tel4, nome: site?.Dados?.tel4_nome, whats: site?.Dados?.tel4_whats },
    ].filter((p) => p.tel);
    const email = site?.Dados?.email1 || site?.Dados?.email2;
    const enderecoLinha = [
        site?.Dados?.endereco,
        [site?.Dados?.bairro, site?.Dados?.cidade].filter(Boolean).join(' - '),
        site?.Dados?.cep,
    ]
        .filter(Boolean)
        .join(', ');
    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const empresa = site?.Dados?.empresa || '';

    return (
        <>
            {!hidePopular && <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin} />}
            {/* BUILDER: âncora estável do bloco "rodape". */}
            <footer className={styles.footer} data-builder-section="rodape">
                <div className={styles.content} style={{ background: corConteudo, color: corTexto }}>
                    <div className={styles.grid}>
                        {/* (a) logo */}
                        <div className={styles.colLogo}>
                            {logoSrc ? (
                                <Link prefetch={false} href={withCodsite('/')} className={styles.logoLink} title={`Imobiliária ${empresa}`}>
                                    <Image
                                        src={logoSrc}
                                        alt={`Imobiliária ${empresa}`}
                                        className={styles.logo}
                                        width={260}
                                        height={110}
                                        unoptimized
                                    />
                                </Link>
                            ) : (
                                <span className={styles.colTitle} style={{ color: corTitulo }}>
                                    {empresa}
                                </span>
                            )}
                        </div>

                        {/* (b) contato */}
                        <div className={styles.col}>
                            {empresa ? (
                                <p className={styles.empresa} style={{ color: corTitulo }}>
                                    {empresa}
                                </p>
                            ) : null}
                            {enderecoLinha ? <p className={styles.linha}>{enderecoLinha}</p> : null}
                            {phones.map((p, idx) => {
                                const href = p.whats
                                    ? `https://api.whatsapp.com/send?phone=55${(p.tel || '').replace(/\D/g, '')}`
                                    : `tel:+55${(p.tel || '').replace(/\D/g, '')}`;
                                return (
                                    <a
                                        key={idx}
                                        className={styles.contato}
                                        href={href}
                                        rel="noopener noreferrer"
                                        target={p.whats ? '_blank' : undefined}
                                        style={{ color: corTexto }}
                                        onClick={trackFooter('Telefone do rodapé', 'footer_contact')}
                                    >
                                        {p.whats ? <FaWhatsapp aria-hidden="true" style={{ color: corTitulo }} /> : null}
                                        {MaskPhone(p.tel)}
                                        {p.nome ? ` — ${p.nome}` : ''}
                                    </a>
                                );
                            })}
                            {email ? (
                                emailVisivel ? (
                                    <a className={styles.contato} href={`mailto:${email}`} style={{ color: corTexto }}>
                                        {email}
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.verEmail}
                                        style={{ color: corTexto }}
                                        onClick={() => setEmailVisivel(true)}
                                    >
                                        Ver e-mail
                                    </button>
                                )
                            ) : null}
                        </div>

                        {/* (c) menu */}
                        <div className={styles.col}>
                            <span className={styles.colTitle} style={{ color: corTitulo }}>
                                Menu
                            </span>
                            <ul className={styles.list}>
                                <li>
                                    <Link prefetch={false} href={withCodsite('/')} className={styles.link} style={{ color: corTexto }} onClick={trackFooter('Início', 'footer_institutional')}>
                                        Início
                                    </Link>
                                </li>
                                {(site?.transacoes || []).map((t: string, idx: number) =>
                                    t ? (
                                        <li key={`t-${idx}`}>
                                            <Link
                                                prefetch={false}
                                                href={withCodsite(`/imoveis/${String(t).toLowerCase()}/-/-/-/-`)}
                                                className={styles.link}
                                                style={{ color: corTexto }}
                                                onClick={trackFooter(`Imóveis para ${String(t).toLowerCase()}`, 'footer_institutional')}
                                            >
                                                Imóveis para {String(t).toLowerCase() === 'aluguel' ? 'alugar' : String(t).toLowerCase()}
                                            </Link>
                                        </li>
                                    ) : null
                                )}
                                {menus.map((m: any, idx: number) => (
                                    <li key={`m-${idx}`}>
                                        <Link
                                            prefetch={false}
                                            href={withCodsite(m?.link ? m.link : `/menu/${m.titulo}`)}
                                            className={styles.link}
                                            style={{ color: corTexto }}
                                            onClick={trackFooter(m.titulo, 'footer_institutional')}
                                        >
                                            {m.titulo}
                                        </Link>
                                    </li>
                                ))}
                                {allDataHome?.blogs?.[0]?.id ? (
                                    <li>
                                        <Link prefetch={false} href={withCodsite('/blog')} className={styles.link} style={{ color: corTexto }} onClick={trackFooter('Blog', 'footer_institutional')}>
                                            Blog
                                        </Link>
                                    </li>
                                ) : null}
                                <li>
                                    <Link prefetch={false} href={withCodsite('/politica')} className={styles.link} style={{ color: corTexto }} onClick={trackFooter('Política de privacidade', 'footer_institutional')}>
                                        Política de privacidade
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* (d) social */}
                        {socials.length > 0 ? (
                            <div className={styles.col}>
                                <span className={styles.colTitle} style={{ color: corTitulo }}>
                                    Social
                                </span>
                                <ul className={styles.list}>
                                    {socials.map((s, idx) => (
                                        <li key={idx}>
                                            <a
                                                href={s.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.link}
                                                style={{ color: corTexto }}
                                                title={`Navegar para o ${s.rede} da imobiliária`}
                                            >
                                                {s.rede}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className={styles.bottomBar} style={{ background: corBarra, color: corBarraText }}>
                    <span>
                        © Copyright {new Date().getFullYear()} - {empresa} - Todos os direitos reservados
                    </span>
                    {site?.Dados?.creci || site?.Dados?.cnpj ? (
                        <span className={styles.bottomMeta}>
                            {site?.Dados?.creci ? `CRECI ${site.Dados.creci}` : ''}
                            {site?.Dados?.creci && site?.Dados?.cnpj ? ' · ' : ''}
                            {site?.Dados?.cnpj ? `CNPJ ${site.Dados.cnpj}` : ''}
                        </span>
                    ) : null}
                    {/* Selo ImobTotal (28/09): linha própria na barra final, alinhado com o ©. */}
                    <SeloImobtotalItem site={site} host={origin} alinhar="esquerda" linhaPropria />
                </div>
            </footer>
        </>
    );
};

class AutoralFooter extends React.Component<IAutoralFooter, { hasError: boolean }> {
    constructor(props: IAutoralFooter) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro rodapé padrão sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <AutoralFooterInner {...this.props} />;
    }
}

export default AutoralFooter;
